import { useState, type ReactNode } from "react";
import { useCustom, usePermissions } from "@refinedev/core";
import { AlertTriangle, CheckCircle2, ChevronRight, Euro, Plus, ReceiptText, Search, X, type LucideIcon } from "lucide-react";

import { VestigingFilter } from "../../components/VestigingFilter";
import { formatEuro } from "../../i18n/format";
import { LEEG, datumMetJaar, type Context } from "./hulp";
import { Laadstaat, Leeg, Pil, Sectie, Verder, type PilToon } from "./Sectie";
import { Rondje } from "./Tegels";
import type { Tint } from "./tegelDefinities";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

type FactuurStatus = "CONCEPT" | "BETAALD" | "OPENSTAAND" | "VERVALLEN";

interface FactuurRegel {
  id: number;
  nummer: string;
  factuurDatum: string | null;
  vervalDatum: string | null;
  status: FactuurStatus;
  totaal: number | null;
  betaald: number | null;
  openstaand: number | null;
  /** Welk onderdeel de factuur maakte: ASIEL, PENSION, CRECHE, LOSSE_VERKOOP of OVERIG. */
  module: string | null;
  /** De vestiging die de factuur maakte; ontbreekt bij een oudere api. */
  vestiging?: string | null;
}

export interface KlantFacturen {
  regels: FactuurRegel[];
  totaalOpenstaand: number;
  totaalBetaald: number;
  totaalVervallen: number;
  aantalOpenstaand: number;
  aantalBetaald: number;
  aantalVervallen: number;
  aantalTotaal: number;
}

const STATUS_TOON: Record<FactuurStatus, PilToon> = {
  OPENSTAAND: "amber",
  VERVALLEN: "rood",
  BETAALD: "groen",
  CONCEPT: "grijs",
};

const STATUSSEN: FactuurStatus[] = ["OPENSTAAND", "VERVALLEN", "BETAALD", "CONCEPT"];

const onderdeelVan = (r: FactuurRegel) => r.module ?? "OVERIG";

/** Zoveel facturen staan er meteen; de rest komt op verzoek. */
const EERST_ZICHTBAAR = 25;

/**
 * Alle facturen van de klant, niet alleen de nieuwste vijftig die de relatietegel toont — anders
 * kloppen de totalen bij een vaste klant niet. De kop van het klantscherm leest dezelfde aanroep.
 */
export function useKlantFacturen(klantId: string) {
  return useCustom<KlantFacturen>({
    url: `relaties/${klantId}/facturen`,
    method: "get",
    config: { query: { alle: true } },
  });
}

/**
 * Het tabblad Facturen: bovenaan de bedragen, daaronder de facturen met zoeken en filteren op status,
 * onderdeel en vestiging. Elk filter is een uitklaplijst met een zoekveld waarin je meerdere keuzes
 * aanvinkt, zoals op het factuuroverzicht; niets gekozen betekent alles. Een factuur openen gaat naar
 * het factuurscherm; een nieuwe factuur maakt een concept voor deze klant en opent dat.
 */
export function FacturenTab({
  klantId,
  ctx,
  onOpenFactuur,
  onNieuweFactuur,
}: {
  klantId: string;
  ctx: Context;
  onOpenFactuur?: (factuurId: string) => void;
  /** Een factuur beginnen voor deze klant -- een leeg scherm, nog geen factuur. */
  onNieuweFactuur?: (klantId: string) => void;
}) {
  const { t, locale } = ctx;
  const { data, isLoading, isError, refetch } = useKlantFacturen(klantId);
  const { data: rechten } = usePermissions<string[]>();
  const magMaken = Array.isArray(rechten) && rechten.includes("ROLE_FACTUUR_LIFECYCLE");
  const [statussen, setStatussen] = useState<string[]>([]);
  const [gekozenOnderdelen, setGekozenOnderdelen] = useState<string[]>([]);
  const [gekozenVestigingen, setGekozenVestigingen] = useState<string[]>([]);
  const [zoek, setZoek] = useState("");
  const [alles, setAlles] = useState(false);

  // "Nieuwe factuur" opent een leeg factuurscherm; de factuur zelf ontstaat pas als je daar op
  // Opslaan drukt. Hier meteen een concept aanmaken liet bij elke misklik een lege factuur
  // achter in de boekhouding (Maarten, 2026-10-24).
  const nieuweFactuur = () => onNieuweFactuur?.(klantId);

  const d = data?.data;
  const regels = Array.isArray(d?.regels) ? d.regels : [];
  const onderdelen = [...new Set(regels.map(onderdeelVan))].sort();
  const vestigingen = [...new Set(regels.map((r) => r.vestiging).filter((v): v is string => !!v))].sort();
  const q = zoek.trim().toLowerCase();
  const gefilterd = regels.filter(
    (r) =>
      (statussen.length === 0 || statussen.includes(r.status)) &&
      (gekozenOnderdelen.length === 0 || gekozenOnderdelen.includes(onderdeelVan(r))) &&
      (gekozenVestigingen.length === 0 || (!!r.vestiging && gekozenVestigingen.includes(r.vestiging))) &&
      (q === "" || r.nummer.toLowerCase().includes(q)),
  );
  const filtersAan = statussen.length > 0 || gekozenOnderdelen.length > 0 || gekozenVestigingen.length > 0 || q !== "";
  const zichtbaar = alles ? gefilterd : gefilterd.slice(0, EERST_ZICHTBAAR);
  const bedrag = (x: number | null | undefined) => (x == null ? LEEG : formatEuro(x, locale));

  return (
    <div className="pk-blad">
      <div className="pk-geld" role="group" aria-label={t("pensionKlant.facturen.totalenAria")}>
        <Geld icoon={Euro} tint="amber" label={t("relatieDetailView.facturen.totaalOpenstaand")} waarde={d ? bedrag(d.totaalOpenstaand) : LEEG}>
          {d && t("relatieDetailView.facturen.countOpenstaand", { args: [d.aantalOpenstaand] })}
        </Geld>
        <Geld icoon={AlertTriangle} tint="rood" label={t("relatieDetailView.facturen.totaalVervallen")} waarde={d ? bedrag(d.totaalVervallen) : LEEG}>
          {d && t("relatieDetailView.facturen.countVervallen", { args: [d.aantalVervallen] })}
        </Geld>
        <Geld icoon={CheckCircle2} tint="groen" label={t("relatieDetailView.facturen.totaalBetaald")} waarde={d ? bedrag(d.totaalBetaald) : LEEG}>
          {d && t("relatieDetailView.facturen.countBetaald", { args: [d.aantalBetaald] })}
        </Geld>
        <Geld icoon={ReceiptText} tint="grijs" label={t("relatieDetailView.facturen.totaalFacturen")} waarde={d ? String(d.aantalTotaal) : LEEG} />
      </div>

      <Sectie
        titel={t("pensionKlant.tab.facturen")}
        icoon={ReceiptText}
        tint="rood"
        aantal={regels.length}
        actie={
          <button
            type="button"
            className="pk-knop pk-knop--hoofd pk-knop--klein"
            disabled={!magMaken || !onNieuweFactuur}
            title={magMaken ? undefined : t("relatieDetailView.facturen.nieuweGeenRecht")}
            onClick={nieuweFactuur}
          >
            <Plus size={15} aria-hidden="true" />
            {t("relatieDetailView.facturen.nieuwe")}
          </button>
        }
      >
        <div className="pk-facturenfilters">
          <label className="pk-zoek pk-facturenfilters__zoek">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              autoComplete="off"
              value={zoek}
              placeholder={t("pensionKlant.facturen.zoek")}
              aria-label={t("pensionKlant.facturen.zoek")}
              onChange={(e) => setZoek(e.target.value)}
            />
          </label>
          <VestigingFilter
            label={t("financieel.filter.status")}
            opties={STATUSSEN}
            gekozen={statussen}
            onChange={setStatussen}
            weergave={(s) => t(`relatieDetailView.facturen.status.${s}`)}
            zoekLabel={t("pensionKlant.facturen.zoekStatus")}
            leegLabel={t("pensionKlant.facturen.geenStatus")}
          />
          {/* Alleen als er iets te kiezen valt: een pensionklant heeft vaak maar één onderdeel. */}
          {onderdelen.length > 1 && (
            <VestigingFilter
              label={t("financieel.filter.onderdeel")}
              opties={onderdelen}
              gekozen={gekozenOnderdelen}
              onChange={setGekozenOnderdelen}
              weergave={(m) => t(`relatieDetailView.facturen.module.${m}`)}
              zoekLabel={t("pensionKlant.facturen.zoekOnderdeel")}
              leegLabel={t("pensionKlant.facturen.geenOnderdeel")}
            />
          )}
          {vestigingen.length > 1 && (
            <VestigingFilter
              label={t("financieel.filter.vestiging")}
              opties={vestigingen}
              gekozen={gekozenVestigingen}
              onChange={setGekozenVestigingen}
              uitlijning="rechts"
            />
          )}
          {filtersAan && (
            <button
              type="button"
              className="pk-wisfilters"
              onClick={() => {
                setStatussen([]);
                setGekozenOnderdelen([]);
                setGekozenVestigingen([]);
                setZoek("");
              }}
            >
              <X size={14} aria-hidden="true" />
              {t("pensionKlant.facturen.wisFilters")}
            </button>
          )}
        </div>

        <Laadstaat laden={isLoading} fout={isError} onOpnieuw={() => refetch()} ctx={ctx} />
        {!isLoading && !isError && gefilterd.length === 0 && (
          <Leeg>{regels.length === 0 ? t("relatieDetailView.facturen.empty") : t("pensionKlant.facturen.geenGevonden")}</Leeg>
        )}
        {zichtbaar.length > 0 && (
          <div className="pk-tabelvak">
            <IndeelbareTabel
              tabelKey="pensionklant-facturen"
              columns={[
                {
                  key: "nummer", header: t("relatieDetailView.facturen.col.nummer"),
                  cell: (r) => (
                    <span className="pk-tabel__sterk">
                      {onOpenFactuur ? (
                        // De rij is klikbaar voor de muis; de knop maakt hem bereikbaar met het toetsenbord.
                        <button
                          type="button"
                          className="pk-factuurlink"
                          aria-label={t("pensionKlant.facturen.openAria", { args: [r.nummer] })}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenFactuur(String(r.id));
                          }}
                        >
                          {r.nummer}
                        </button>
                      ) : (
                        r.nummer
                      )}
                    </span>
                  ),
                },
                { key: "datum", header: t("relatieDetailView.facturen.col.datum"),
                  cell: (r) => datumMetJaar(r.factuurDatum, locale) },
                { key: "verval", header: t("relatieDetailView.facturen.col.verval"),
                  cell: (r) => datumMetJaar(r.vervalDatum, locale) },
                // Onderdeel en vestiging stonden er alleen als er iets te kiezen viel; dat blijft zo.
                ...(onderdelen.length > 1 ? [{
                  key: "onderdeel", header: t("pensionKlant.facturen.onderdeel"),
                  cell: (r: typeof zichtbaar[number]) => t(`relatieDetailView.facturen.module.${onderdeelVan(r)}`),
                }] : []),
                ...(vestigingen.length > 1 ? [{
                  key: "vestiging", header: t("pensionKlant.facturen.vestiging"),
                  cell: (r: typeof zichtbaar[number]) => r.vestiging ?? LEEG,
                }] : []),
                {
                  key: "status", header: t("relatieDetailView.facturen.col.status"),
                  cell: (r) => (
                    <Pil toon={STATUS_TOON[r.status]}>{t(`relatieDetailView.facturen.status.${r.status}`)}</Pil>
                  ),
                },
                { key: "bedrag", header: t("relatieDetailView.facturen.col.bedrag"),
                  cell: (r) => <span className="pk-tabel__bedrag">{bedrag(r.totaal)}</span> },
                { key: "betaald", header: t("pensionKlant.facturen.betaald"),
                  cell: (r) => <span className="pk-tabel__bedrag">{bedrag(r.betaald)}</span> },
                {
                  key: "openstaand", header: t("pensionKlant.facturen.openstaand"),
                  cell: (r) => (
                    <span className="pk-tabel__bedrag" data-open={(r.openstaand ?? 0) > 0 || undefined}>
                      {bedrag(r.openstaand)}
                    </span>
                  ),
                },
                {
                  key: "pijl", header: "", vast: true,
                  cell: () => (onOpenFactuur
                    ? <span className="pk-tabel__pijl" aria-hidden="true"><ChevronRight size={16} /></span>
                    : null),
                },
              ]}
              rows={zichtbaar}
              getRowKey={(r) => String(r.id)}
              onRowClick={onOpenFactuur ? (r) => onOpenFactuur(String(r.id)) : undefined}
              empty=""
            />
          </div>
        )}
        {gefilterd.length > EERST_ZICHTBAAR && (
          <Verder onClick={() => setAlles((x) => !x)}>
            {alles ? t("pensionKlant.blad.minder") : t("pensionKlant.blad.alle", { args: [gefilterd.length] })}
          </Verder>
        )}
      </Sectie>
    </div>
  );
}

function Geld({
  icoon,
  tint,
  label,
  waarde,
  children,
}: {
  icoon: LucideIcon;
  tint: Tint;
  label: string;
  waarde: string;
  children?: ReactNode;
}) {
  return (
    <div className="pk-tegel pk-geld__vak">
      <Rondje icoon={icoon} tint={tint} grootte="kop" />
      <div className="pk-geld__tekst">
        <span className="pk-geld__label">{label}</span>
        <span className="pk-geld__waarde">{waarde}</span>
        {children && <span className="pk-geld__aantal">{children}</span>}
      </div>
    </div>
  );
}
